import type {ItemObject, ItemType} from '@PosterWhiteboard/items/item/item.types';
import {ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import type {Page} from '@PosterWhiteboard/page/page.class';
import type {VideoItem} from '@PosterWhiteboard/items/video-item/video-item.class';
import type {CornerPoints} from '@Utils/math.util';
import {getCommonAreaOfRectangles} from '@Utils/math.util';
import type {ImageItem} from '@PosterWhiteboard/items/image-item/image-item.class';
import type {SlideshowItem} from '@PosterWhiteboard/items/slideshow-item/slideshow-item.class';
import type {TextItem} from '@PosterWhiteboard/items/text-item/text-item.class';
import {AddItem} from '@PosterWhiteboard/page/add-item.class';
import type {UpdateFromObjectOpts} from '@PosterWhiteboard/common.types';
import {hideLoading, showLoading} from '@Libraries/loading-toast-library';
import {openMessageModal} from '@Modals/message-modal';
import {ReplaceItem} from '@PosterWhiteboard/page/replace-item.class';
import type {StickerItem} from '@PosterWhiteboard/items/sticker-item.class';
import {createItemFromObject} from '@PosterWhiteboard/items/item/item-factory';
import type {VideoSlideItem} from '@PosterWhiteboard/items/slideshow-item/slide-items/video-slide-item.class';
import type {FabricObject, Group} from '@postermywall/fabricjs-2';
import type {TranscriptItem} from '@PosterWhiteboard/items/transcript-item/transcript-item';
import type {DeepPartial} from '@/global';
import {deleteItemByID} from '@Components/poster-editor/library/poster-editor-library';
import {noop} from '@Utils/general.util';
import {getAnimationTime} from '@PosterWhiteboard/classes/item-animation.class';
import {calculateIntroStartTimes, calculateOutroEndTimes, isAnimationDurationApplicableOnItem} from '@PosterWhiteboard/page/page-animation.util';

export interface PageItemsObject {
  itemsHashMap: Record<any, ItemObject>;
  itemsOrder: string[];
}

export class PageItems {
  public itemsHashMap: Record<string, ItemType> = {};
  public page: Page;
  public addItems: AddItem;
  public replaceItems: ReplaceItem;

  public constructor(page: Page) {
    this.page = page;
    this.addItems = new AddItem(page);
    this.replaceItems = new ReplaceItem(page);
  }

  public getItemIdsInOrder(): string[] {
    const objs = this.page.fabricCanvas.getObjects();
    const itemsInOrder = [];

    for (let i = 0; i < objs.length; i++) {
      if (objs[i].__PMWID) {
        itemsInOrder.push(objs[i].__PMWID);
      }
    }
    return itemsInOrder;
  }

  public getItemsInOrder(): ItemType[] {
    const itemIdsInOrder = this.getItemIdsInOrder();
    const itemsInOrder: ItemType[] = [];

    for (const id of itemIdsInOrder) {
      const item = this.getItem(id);
      if (item) {
        itemsInOrder.push(item);
      }
    }

    return itemsInOrder;
  }

  public getItemOrder(itemId: string): number {
    // This can come from reinit flow before item fabricObject is available

    return this.page.fabricCanvas.getObjects().indexOf(this.itemsHashMap[itemId]?.fabricObject);
  }

  public addItem(newItem: ItemType): void {
    this.itemsHashMap[newItem.uid] = newItem;
  }

  public getLongestVideoItem(): VideoItem | null {
    let longestVideo: VideoItem | null = null;
    const itemsIds = Object.keys(this.itemsHashMap);

    itemsIds.forEach((itemId) => {
      if (this.itemsHashMap[itemId].isVideo() && (!longestVideo || this.itemsHashMap[itemId].getPlayDuration() > longestVideo.getPlayDuration())) {
        longestVideo = this.itemsHashMap[itemId];
      }
    });

    return longestVideo;
  }

  public getLongestStreamingSlideshowItem(): SlideshowItem | undefined {
    let longestSlideshowItem: SlideshowItem | undefined;
    const itemsIds = Object.keys(this.itemsHashMap);

    itemsIds.forEach((itemId) => {
      const item = this.itemsHashMap[itemId];
      if (item.isSlideshow() && item.isStreamingItem() && (!longestSlideshowItem || item.getPlayDuration() > longestSlideshowItem.getPlayDuration())) {
        longestSlideshowItem = item;
      }
    });

    return longestSlideshowItem;
  }

  public getLongestVideoItemDuration(): number {
    const item = this.getLongestVideoItem();
    return item ? item.getDuration() : 0;
  }

  public getItemForFabricObject(fabricItem: FabricObject): ItemType | undefined {
    return this.itemsHashMap[fabricItem.__PMWID];
  }

  public getLongestSlideshowItemDuration(): number {
    const item = this.getLongestStreamingSlideshowItem();
    return item ? item.getDuration() : 0;
  }

  public hasVideoItem(): boolean {
    for (const [, item] of Object.entries(this.itemsHashMap)) {
      if (item.isVideo()) {
        return true;
      }
    }
    return false;
  }

  public hasMenuItems(): boolean {
    for (const [, item] of Object.entries(this.itemsHashMap)) {
      if (item.isMenu()) {
        return true;
      }
    }
    return false;
  }

  public hasClickableItem(): boolean {
    return Object.values(this.itemsHashMap).some((item) => {
      return item.hasClickableLink();
    });
  }

  public hasSlideshowItemContainingVideoSlide(): boolean {
    for (const [, item] of Object.entries(this.itemsHashMap)) {
      if (item.isSlideshow() && item.slides.hasVideoSlide()) {
        return true;
      }
    }
    return false;
  }

  public hasAnimatedStickerItem(): boolean {
    for (const [, item] of Object.entries(this.itemsHashMap)) {
      if (item.isSticker()) {
        return true;
      }
    }
    return false;
  }

  public hasTranscriptItem(): boolean {
    for (const [, item] of Object.entries(this.itemsHashMap)) {
      if (item.isTranscript()) {
        return true;
      }
    }
    return false;
  }

  public hasSlideshowItem(): boolean {
    for (const [, item] of Object.entries(this.itemsHashMap)) {
      if (item.isSlideshow()) {
        return true;
      }
    }
    return false;
  }

  public hasStreamingSlideShowItem(): boolean {
    const itemsIds = Object.keys(this.itemsHashMap);

    for (const itemId of itemsIds) {
      const item = this.itemsHashMap[itemId];
      if (item.isSlideshow() && item.isStreamingItem()) {
        return true;
      }
    }
    return false;
  }

  public hasItem(uid: string): boolean {
    return this.itemsHashMap[uid] !== undefined;
  }

  public getVideoContainingItems(): ItemType[] {
    return Object.values(this.itemsHashMap).filter((item) => {
      return item.isVideo() || (item.isSlideshow() && item.slides.hasVideoSlide()) ? item : null;
    });
  }

  public getNumberOfVideoContainingItems(): number {
    return this.getVideoContainingItems().length;
  }

  public getItem(uid: string): ItemType | undefined {
    if (!this.hasItem(uid)) {
      return undefined;
    }

    return this.itemsHashMap[uid];
  }

  public getGettyImageCount(): number {
    let count = 0;

    const itemsIds = Object.keys(this.itemsHashMap);
    for (const itemId of itemsIds) {
      const item = this.itemsHashMap[itemId];
      if (item.isImage() && item.fabricObject.visible && item.isNonPurchasedGettyImage()) {
        count += 1;
      } else if (item.isSlideshow()) {
        count += item.slides.getUnpuchasedGettyImageSlidesCount();
      }
    }

    return count;
  }

  public getExtractedGettyStickerCount(): number {
    let count = 0;

    const itemsIds = Object.keys(this.itemsHashMap);
    for (const itemId of itemsIds) {
      const item = this.itemsHashMap[itemId];
      if (item.isImage() && item.fabricObject.visible && item.isNonPurchasedExtractedGettySticker()) {
        count += 1;
      }
    }

    return count;
  }

  public getGettyVideoCount(): number {
    let count = 0;

    const itemsIds = Object.keys(this.itemsHashMap);
    for (const itemId of itemsIds) {
      const item = this.itemsHashMap[itemId];
      if (item.isVideo() && item.hasGettyContent()) {
        count += 1;
      } else if (item.isSlideshow()) {
        count += item.slides.getGettyVideoSlidesCount();
      }
    }

    return count;
  }

  public toObject(): PageItemsObject {
    const itemObjects: Record<string, ItemObject> = {};

    for (const [key, item] of Object.entries(this.itemsHashMap)) {
      itemObjects[key] = item.toObject();
    }

    return {
      itemsHashMap: itemObjects,
      itemsOrder: this.getItemIdsInOrder(),
    };
  }

  public toObjectWithNewUids(): PageItemsObject {
    const itemObjects: Record<string, ItemObject> = {};
    const uidMap = new Map<string, string>();

    for (const [oldUid, item] of Object.entries(this.itemsHashMap)) {
      const itemObject = item.toObjectWithNewUids() as ItemObject;
      const newUid = itemObject.uid;
      uidMap.set(oldUid, newUid);
      itemObjects[newUid] = itemObject;
    }

    const itemsOrder = this.getItemIdsInOrder().map((uid: string) => {
      return uidMap.get(uid) || uid;
    });

    return {
      itemsHashMap: itemObjects,
      itemsOrder,
    };
  }

  public async updateFromObject(
    itemsObject: DeepPartial<PageItemsObject> = {},
    {updateRedux = true, fireModifiedEvent = true, undoable = true, refreshActiveSelection = false, checkForDurationUpdate = false, onItemAddFail = noop}: UpdateFromObjectOpts = {}
  ): Promise<void> {
    // delete items that are in this page but not in the itemsObject
    for (const [uid] of Object.entries(this.itemsHashMap)) {
      if (itemsObject.itemsHashMap?.[uid] === undefined) {
        this.doRemoveItem(uid);
      }
    }

    // Update/Add items from itemsObject
    const updateFromObjectPromises = [];
    const selectedViews = this.page.activeSelection.getSelectedViews() as (FabricObject | Group)[];
    let itemsAreInActiveSelection = false;
    if (itemsObject.itemsHashMap) {
      const addItemPromises = [];

      if (refreshActiveSelection && selectedViews.length > 1) {
        itemsAreInActiveSelection = true;
        this.page.poster.itemsMultiSelect.isActiveSelectionModificationProcessing = true;
        this.page.activeSelection.clearSelection();
      }
      for (const [uid, itemObject] of Object.entries(itemsObject.itemsHashMap)) {
        if (itemObject) {
          if (this.itemsHashMap[uid] !== undefined) {
            updateFromObjectPromises.push(
              this.itemsHashMap[uid].updateFromObject(itemObject, {
                updateRedux: false,
                undoable: false,
                fireModifiedEvent: false,
                checkForDurationUpdate,
              })
            );
          } else {
            const addItemPromise = new Promise<void>((resolve, reject) => {
              showLoading(`addItem${uid}`);
              createItemFromObject(this.page, itemObject)
                .then(async (itemToAdd) => {
                  await this.page.addItem(itemToAdd, {
                    updateRedux: true,
                    undoable: false,
                    fireEvent: false,
                    checkForDurationUpdate,
                  });
                  resolve();
                })
                .catch(reject)
                .finally(() => {
                  hideLoading(`addItem${uid}`);
                });
            });
            if (this.page.poster.mode.isGeneration()) {
              try {
                await addItemPromise;
              } catch (e) {
                console.error(e);
                throw e;
              }
            } else {
              addItemPromises.push(addItemPromise);
            }
          }
        }
      }

      const addItemPromisesResponses = await Promise.allSettled(addItemPromises);
      for (const addItemPromisesResponse of addItemPromisesResponses) {
        if (addItemPromisesResponse.status === 'rejected') {
          console.error(addItemPromisesResponse.reason);
          onItemAddFail();
          break;
        }
      }
    }

    if (itemsObject.itemsOrder) {
      itemsObject.itemsOrder.forEach((itemId, i) => {
        if (itemId) {
          const item = this.itemsHashMap[itemId];
          if (item) {
            this.moveItemTo(item, i);
          }
        }
      });
    }

    await Promise.all(updateFromObjectPromises);

    if (itemsAreInActiveSelection) {
      const filteredViews = this.page.getFilteredExistingViews(selectedViews);
      if (filteredViews.length) {
        this.page.activeSelection.selectFabricObjects(filteredViews);
      }
      this.page.poster.itemsMultiSelect.isActiveSelectionModificationProcessing = false;
    }

    if (undoable) {
      this.page.poster.history.addPosterHistory();
    }
    if (fireModifiedEvent) {
      this.page.fire('modified');
    }
    if (updateRedux) {
      this.page.poster.redux.updateReduxData();
    }
  }

  public moveItemTo(item: ItemType, index: number): void {
    const objs = this.page.fabricCanvas.getObjects();
    let currentIndexForPMWItems = 0;

    for (const [i, obj] of objs.entries()) {
      if (currentIndexForPMWItems === index) {
        this.page.fabricCanvas.moveObjectTo(item.fabricObject, i);
        return;
      }
      if (obj.__PMWID) {
        currentIndexForPMWItems += 1;
      }
    }

    this.page.fabricCanvas.moveObjectTo(item.fabricObject, objs.length);
  }

  public getAllTranscriptItems(): TranscriptItem[] {
    const transcriptItems: TranscriptItem[] = [];

    Object.values(this.itemsHashMap).forEach((item) => {
      if (item.isTranscript()) {
        transcriptItems.push(item);
      }
    });

    return transcriptItems;
  }

  private onZIndexModified(): void {
    this.page.poster.history.addPosterHistory();
    this.page.poster.redux.updateReduxData();
  }

  public removeItem(itemUid: string, undoable = true): void {
    const item = this.getItem(itemUid);
    if (item) {
      if ((item.isVideo() || item.isTranscript()) && this.page.poster.mode.isRegen()) {
        openMessageModal({
          title: window.i18next.t('pmwjs_delete_item_disabled_title'),
          text: window.i18next.t('pmwjs_delete_item_disabled_message'),
        });
        return;
      }

      this.doRemoveItem(itemUid);

      if (item.isVideo() || item.isSlideshow()) {
        this.page.calculateAndUpdatePageDuration();
      }
      if (item.isPremium()) {
        this.page.pageWatermark.refreshWatermark();
      }
      if (undoable) {
        this.page.poster.history.addPosterHistory();
      }
      this.page.fire('modified');
      this.page.poster.redux.updateReduxData();
    }
  }

  public stopItemsAnimation(): void {
    this.getItems().forEach((item) => {
      item.stopItemAnimation();
    });
  }

  private doRemoveItem(itemUid: string): void {
    if (this.hasItem(itemUid)) {
      this.itemsHashMap[itemUid].onRemove();
      this.page.activeSelection.removeItemFromSelection(this.itemsHashMap[itemUid].fabricObject);
      this.page.fabricCanvas.remove(this.itemsHashMap[itemUid]?.fabricObject);
      delete this.itemsHashMap[itemUid];
    }
  }

  public getItems(): ItemType[] {
    return Object.values(this.itemsHashMap);
  }

  public getTextItems(): TextItem[] {
    const items = [];
    for (const [, item] of Object.entries(this.itemsHashMap)) {
      if (item.isText()) {
        items.push(item);
      }
    }
    return items;
  }

  public getImageItems(): ImageItem[] {
    const items = [];
    for (const [, item] of Object.entries(this.itemsHashMap)) {
      if (item.isImage()) {
        items.push(item);
      }
    }
    return items;
  }

  public getStickerItems(): StickerItem[] {
    const stickers: StickerItem[] = [];
    for (const [, item] of Object.entries(this.itemsHashMap)) {
      if (item.isSticker()) {
        stickers.push(item);
      }
    }
    return stickers;
  }

  public getSlideshowItems(): SlideshowItem[] {
    const slideshowItems: SlideshowItem[] = [];
    for (const [, item] of Object.entries(this.itemsHashMap)) {
      if (item.isSlideshow()) {
        slideshowItems.push(item);
      }
    }
    return slideshowItems;
  }

  public getVideoSlides(): VideoSlideItem[] {
    const slideshowItems: VideoSlideItem[] = [];
    for (const [, item] of Object.entries(this.itemsHashMap)) {
      if (item.isSlideshow()) {
        slideshowItems.push(...item.slides.getVideoSlides());
      }
    }
    return slideshowItems;
  }

  public getVideoItems(): VideoItem[] {
    const videoItems: VideoItem[] = [];
    for (const [, item] of Object.entries(this.itemsHashMap)) {
      if (item.isVideo()) {
        videoItems.push(item);
      }
    }
    return videoItems;
  }

  public getGenerationSeekableItems(): (StickerItem | TranscriptItem)[] {
    const generationSeekableItems: (StickerItem | TranscriptItem)[] = [];
    for (const [, item] of Object.entries(this.itemsHashMap)) {
      if (item.isSticker() || item.isTranscript()) {
        generationSeekableItems.push(item);
      }
    }
    return generationSeekableItems;
  }

  public isVideoItemUsedAsBackground(): boolean {
    if (!this.hasVideoItem()) {
      return false;
    }

    const canvasVertices = this.page.getCanvasCornerPoints();
    const MAX_NON_VIDEO_BACKGROUND_RATIO = 0.7;
    let graphicItemArea;
    let graphicItemAreaRatio;

    const itemsIds = Object.keys(this.itemsHashMap);

    for (const itemId of itemsIds) {
      if (this.itemsHashMap[itemId].isVideo()) {
        const videoItem = this.itemsHashMap[itemId];
        const graphicItemVertices = videoItem.fabricObject.getCornerPoints(videoItem.fabricObject.getCenterPoint());
        // reformatting them according to doPolygonsIntersect() params format requirement
        const graphicItemCornerPoints: CornerPoints = {
          tl: {
            x: graphicItemVertices.tl.x,
            y: graphicItemVertices.tl.y,
          },
          tr: {
            x: graphicItemVertices.tr.x,
            y: graphicItemVertices.tr.y,
          },
          br: {
            x: graphicItemVertices.br.x,
            y: graphicItemVertices.br.y,
          },
          bl: {
            x: graphicItemVertices.bl.x,
            y: graphicItemVertices.bl.y,
          },
        };
        graphicItemArea = getCommonAreaOfRectangles(canvasVertices, graphicItemCornerPoints);
        graphicItemAreaRatio = graphicItemArea / (canvasVertices.bl.x * canvasVertices.bl.y);

        if (graphicItemAreaRatio > MAX_NON_VIDEO_BACKGROUND_RATIO) {
          return true;
        }
      }
    }
    return false;
  }

  public getZIndexMovableTopItem(): ItemType | undefined {
    const objs = this.page.fabricCanvas.getObjects();

    for (let i = objs.length - 1; i >= 0; i--) {
      if (objs[i].__PMWID) {
        const item = this.itemsHashMap[objs[i].__PMWID];
        if (item.canMoveInZIndex()) {
          return this.itemsHashMap[objs[i].__PMWID];
        }
      }
    }
    return undefined;
  }

  public getZIndexMovableBottomItem(): ItemType | undefined {
    const objs = this.page.fabricCanvas.getObjects();

    for (const obj of objs) {
      if (obj.__PMWID) {
        const item = this.itemsHashMap[obj.__PMWID];
        if (item.canMoveInZIndex()) {
          return this.itemsHashMap[obj.__PMWID];
        }
      }
    }
    return undefined;
  }

  public getNumberOfItemsOnPage(): number {
    const itemsIds = Object.keys(this.itemsHashMap);
    return itemsIds.length;
  }

  public canSelectedItemsBeMovedForward = (): boolean => {
    if (this.page.items.getNumberOfItemsOnPage() <= 1) {
      return false;
    }

    const selectedItems = this.page.getSelectedZIndexMovableItems();
    if (selectedItems.length === 0) {
      return false;
    }

    const activeSelection = this.page.activeSelection.getActiveObject();
    if (activeSelection?.lockMovementX) {
      return false;
    }

    const topItemZIndex = this.page.items.getZIndexMovableTopItem()?.fabricObject.getZIndex();
    if (topItemZIndex === undefined) {
      return false;
    }

    for (const [index, selectedItem] of selectedItems.entries()) {
      if (selectedItem.fabricObject.getZIndex() !== topItemZIndex - (selectedItems.length - (index + 1))) {
        return true;
      }
    }

    return false;
  };

  public canSelectedItemsBeMovedBackwards = (): boolean => {
    if (this.page.items.getNumberOfItemsOnPage() <= 1) {
      return false;
    }

    const selectedItems = this.page.getSelectedZIndexMovableItems();
    if (selectedItems.length === 0) {
      return false;
    }

    const activeSelection = this.page.activeSelection.getActiveObject();
    if (activeSelection?.lockMovementX) {
      return false;
    }

    const bottomItemZIndex = this.page.items.getZIndexMovableBottomItem()?.fabricObject.getZIndex();
    if (bottomItemZIndex === undefined) {
      return false;
    }

    for (const [index, selectedItem] of selectedItems.entries()) {
      if (selectedItem.fabricObject.getZIndex() !== bottomItemZIndex + index) {
        return true;
      }
    }

    return false;
  };

  public sendBackwardsSelectedItem(): void {
    const selectedItems = this.page.getSelectedZIndexMovableItems();

    if (selectedItems.length > 0) {
      const itemIdsInOrder = this.getItemIdsInOrder();
      if (selectedItems.length === 1) {
        this.page.fabricCanvas.sendObjectBackwards(selectedItems[0].fabricObject, true);
      } else {
        const lowestZIndexSelectedItem = selectedItems[0];
        this.page.fabricCanvas.sendObjectBackwards(lowestZIndexSelectedItem.fabricObject);
        const newLowestZindex = this.page.fabricCanvas.getObjects().indexOf(lowestZIndexSelectedItem.fabricObject);

        for (const [index, item] of selectedItems.entries()) {
          this.page.fabricCanvas.moveObjectTo(item.fabricObject, newLowestZindex + index);
        }
      }

      if (this.getItemIdsInOrder().toString() !== itemIdsInOrder.toString()) {
        this.onZIndexModified();
      }
    }
  }

  public bringForwardSelectedItem(): void {
    const selectedItems = this.page.getSelectedZIndexMovableItems();

    if (selectedItems.length > 0) {
      const itemIdsInOrder = this.getItemIdsInOrder();
      if (selectedItems.length === 1) {
        this.page.fabricCanvas.bringObjectForward(selectedItems[0].fabricObject, true);
      } else {
        const highestZIndexSelectedItem = selectedItems[selectedItems.length - 1];
        this.page.fabricCanvas.bringObjectForward(highestZIndexSelectedItem.fabricObject);
        const newHighestZindex = this.page.fabricCanvas.getObjects().indexOf(highestZIndexSelectedItem.fabricObject);

        for (const [index, item] of selectedItems.entries()) {
          this.page.fabricCanvas.moveObjectTo(item.fabricObject, newHighestZindex - (selectedItems.length - (index + 1)));
        }
      }

      if (this.getItemIdsInOrder().toString() !== itemIdsInOrder.toString()) {
        this.onZIndexModified();
      }
    }
  }

  public bringToFrontSelectedItem(): void {
    const selectedItems = this.page.getSelectedZIndexMovableItems();

    if (selectedItems.length > 0) {
      const itemIdsInOrder = this.getItemIdsInOrder();
      for (const item of selectedItems) {
        this.page.fabricCanvas.bringObjectToFront(item.fabricObject);
      }

      if (this.getItemIdsInOrder().toString() !== itemIdsInOrder.toString()) {
        this.onZIndexModified();
      }
    }
  }

  public sendToBackSelectedItem(): void {
    const selectedItems = this.page.getSelectedZIndexMovableItems();

    if (selectedItems.length > 0) {
      const itemIdsInOrder = this.getItemIdsInOrder();
      for (let i = selectedItems.length - 1; i >= 0; i--) {
        this.page.fabricCanvas.sendObjectToBack(selectedItems[i].fabricObject);
      }

      if (this.getItemIdsInOrder().toString() !== itemIdsInOrder.toString()) {
        this.onZIndexModified();
      }
    }
  }

  public hasLockedItems(): boolean {
    for (const [, item] of Object.entries(this.itemsHashMap)) {
      if (item.fabricObject.lockMovementX) {
        return true;
      }
    }
    return false;
  }

  public unLockAllLockedItems(): void {
    const itemsHashMapObject = this.toObject().itemsHashMap;

    for (const [, item] of Object.entries(itemsHashMapObject)) {
      if (item.lockMovementX) {
        item.lockMovementX = false;
        item.lockMovementY = false;
        item.lockRotation = false;
        item.lockScalingX = false;
        item.lockScalingY = false;
      }
    }

    void this.updateFromObject({itemsHashMap: itemsHashMapObject});
  }

  public getNumberOfTranscriptItems(): number {
    const itemsHashMapObject = this.toObject().itemsHashMap;
    let numItems = 0;

    for (const [, item] of Object.entries(itemsHashMapObject)) {
      if (item.gitype === ITEM_TYPE.TRANSCRIPT) {
        numItems += 1;
      }
    }

    return numItems;
  }

  public getTranscriptItemIds(): string[] {
    const itemsHashMapObject = this.toObject().itemsHashMap;
    const ids: string[] = [];

    for (const [, item] of Object.entries(itemsHashMapObject)) {
      if (item.gitype === ITEM_TYPE.TRANSCRIPT) {
        ids.push(item.uid);
      }
    }

    return ids;
  }

  public async trimAllTranscriptItemsExceedingPageDuration(fireModifiedEvent = true): Promise<void> {
    const promises: Promise<void>[] = [];
    for (const [, item] of Object.entries(this.itemsHashMap)) {
      if (item.isTranscript()) {
        promises.push(item.trimSubtitlesExceedingDuration(this.page.getDuration(), fireModifiedEvent));
      }
    }

    await Promise.all(promises);
  }

  public updateItemsAnimation(): void {
    if (this.pageItemHasIntroAnimation()) {
      const introResult = calculateIntroStartTimes(this.itemsHashMap, this.page.fabricCanvas, this.page.getDuration(), this.isVideoItemUsedAsBackground());
      for (const [uid, startTime] of Object.entries(introResult.startTimes)) {
        if (this.itemsHashMap[uid] && isAnimationDurationApplicableOnItem(this.itemsHashMap[uid])) {
          this.itemsHashMap[uid].onPageStartTime = startTime;
        }
      }
      for (const [uid, direction] of Object.entries(introResult.directionUpdates)) {
        this.itemsHashMap[uid]?.introAnimation.copyVals({direction});
      }
    }
    if (this.pageItemHasOutroAnimation()) {
      const outroResult = calculateOutroEndTimes(this.itemsHashMap, this.page.fabricCanvas, this.page.getDuration());
      for (const [uid, endTime] of Object.entries(outroResult.endTimes)) {
        if (this.itemsHashMap[uid] && isAnimationDurationApplicableOnItem(this.itemsHashMap[uid])) {
          this.itemsHashMap[uid].onPageEndTime = endTime;
        }
      }
      for (const [uid, direction] of Object.entries(outroResult.directionUpdates)) {
        this.itemsHashMap[uid]?.outroAnimation.copyVals({direction});
      }
    }
  }

  public updateItemsDuration(duration?: number, oldDuration?: number): void {
    if (duration === undefined) {
      return;
    }
    const compareDuration = oldDuration ?? this.page.getDuration();
    Object.keys(this.itemsHashMap).forEach((key) => {
      const item = this.itemsHashMap[key];
      if (item.onPageEndTime >= compareDuration) {
        item.onPageEndTime = duration;
      }
    });

    if (this.pageItemHasOutroAnimation()) {
      const outroResult = calculateOutroEndTimes(this.itemsHashMap, this.page.fabricCanvas, duration);
      for (const [uid, endTime] of Object.entries(outroResult.endTimes)) {
        if (this.itemsHashMap[uid] && isAnimationDurationApplicableOnItem(this.itemsHashMap[uid])) {
          this.itemsHashMap[uid].onPageEndTime = endTime;
        }
      }
    }
  }

  public getMaxAnimationTime(): number {
    const MIN_DURATION_BETWEEN_ANIMATIONS = 1;
    return this.getMaxIntroAnimationTime() + this.getMaxOutroAnimationTime() + MIN_DURATION_BETWEEN_ANIMATIONS;
  }

  private getMaxIntroAnimationTime(): number {
    if (this.pageItemHasIntroAnimation()) {
      let maxAnimation = 0;
      Object.keys(this.itemsHashMap).forEach((key) => {
        const item = this.itemsHashMap[key];
        if (item.hasIntroAnimation()) {
          maxAnimation = Math.max(maxAnimation, item.onPageStartTime + getAnimationTime(item.introAnimation.type, item.introAnimation.speed));
        }
      });
      return maxAnimation;
    }
    return 0;
  }

  private getMaxOutroAnimationTime(): number {
    if (this.pageItemHasOutroAnimation()) {
      let maxAnimation = 0;
      Object.keys(this.itemsHashMap).forEach((key) => {
        const item = this.itemsHashMap[key];
        if (item.hasOutroAnimation()) {
          maxAnimation = Math.max(maxAnimation, getAnimationTime(item.outroAnimation.type, item.outroAnimation.speed));
        }
      });
      return maxAnimation;
    }
    return 0;
  }

  public hasMotionOrAnimatingItems(): boolean {
    return this.pageItemHasAnimation() || this.hasVideoItem() || this.hasSlideshowItem() || this.hasTranscriptItem() || this.hasAnimatedStickerItem();
  }

  public pageItemHasAnimation(): boolean {
    return Object.values(this.itemsHashMap).some((item) => item.hasAnimation());
  }

  public pageItemHasIntroAnimation(): boolean {
    return Object.values(this.itemsHashMap).some((item) => item.hasIntroAnimation());
  }

  public pageItemHasOutroAnimation(): boolean {
    return Object.values(this.itemsHashMap).some((item) => item.hasOutroAnimation());
  }

  public pageItemHasIntroOutroAnimation(): boolean {
    return Object.values(this.itemsHashMap).some((item) => item.hasIntroAnimation() && item.hasOutroAnimation());
  }
}

export const deleteTranscriptItemWithSubtitleId = (subtitleId: string): void => {
  const items = window.posterEditor?.whiteboard?.getCurrentPage().items.itemsHashMap;
  if (!items) {
    return;
  }
  const transcriptItems = Object.values(items).filter((item) => item.gitype === ITEM_TYPE.TRANSCRIPT) as TranscriptItem[];

  for (const transcriptItem of transcriptItems) {
    for (const subtitle of Object.values(transcriptItem.subtitlesHashmap)) {
      if (subtitle.subtitleUID === subtitleId) {
        deleteItemByID(transcriptItem.uid);
        return;
      }
    }
  }
};

export const deleteSubtitleById = (subtitleId: string): boolean => {
  const items = window.posterEditor?.whiteboard?.getCurrentPage().items.itemsHashMap;
  if (!items) {
    return false;
  }

  const transcriptItems = Object.values(items).filter((item) => item.gitype === ITEM_TYPE.TRANSCRIPT) as TranscriptItem[];

  for (const transcriptItem of transcriptItems) {
    if (transcriptItem.subtitlesHashmap[subtitleId]) {
      return transcriptItem.deleteSubtitle(subtitleId);
    }
  }

  return false;
};
